// "AI asistent" page (/ai): questions in plain Czech over the ERP data, an overview of the other AI tools
// and – for admins / managers – AI usage and estimated cost. Conversation history lives only in this page.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Send, RotateCcw, AlertCircle, FileUp, Link2, FileEdit, FileText, BarChart3 } from 'lucide-react';
import { aiApi } from '../utils/aiApi';
import AiDocumentIntake from '../components/ai/AiDocumentIntake';

const ACCENT = '#E53935';
const QUESTION_MAX = 1000;
const HISTORY_PAIRS = 6;
const HISTORY_TEXT_MAX = 2000;

const EXAMPLES = [
  'Které trenažéry jsou ve výrobě?',
  'Kdo ještě nemá podepsanou smlouvu?',
  'Kolik TR4 jsme dodali letos v Ústeckém kraji?',
  'Které díly chybí v otevřených objednávkách?',
];

const REFERENCE_PATHS = {
  customer: (id) => `/customers/${id}`,
  machine: (id) => `/machines/${id}`,
  order: () => '/orders',
  part: () => '/parts',
  supplier: () => '/suppliers',
  delivery: (id) => `/odvozy/${id}`,
};

const CONFIDENCE = {
  high: { label: 'Jistá odpověď', color: '#2E7D32', bg: '#E8F5E9' },
  medium: { label: 'Částečně jistá', color: '#8D6E00', bg: '#FFF8E1' },
  low: { label: 'Nejistá – ověřte v ERP', color: '#616161', bg: '#F5F5F5' },
};

const FEATURE_LABELS = [
  ['insights_ask', 'AI asistent'],
  ['insights_summary', 'AI shrnutí'],
  ['documents', 'Příjem dokumentů'],
  ['orders', 'Párování dokladů'],
  ['pdf', 'Úprava PDF'],
];
const featureLabel = (f) => FEATURE_LABELS.find(([prefix]) => String(f).startsWith(prefix))?.[1] || f;

const fmtInt = (n) => Number(n || 0).toLocaleString('cs-CZ');
const fmtUsd = (n) => (n == null ? '–' : `$${Number(n).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const card = { backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0', borderRadius: '12px', padding: '16px' };
const chip = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '999px', border: '1px solid #E0E0E0',
  backgroundColor: '#FFFFFF', color: '#444444', fontSize: '13px', cursor: 'pointer', textDecoration: 'none', lineHeight: 1.3, textAlign: 'left',
};
const sectionTitle = { fontSize: '13px', fontWeight: 600, color: '#888888', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 10px' };

const PAGE_CSS = `
.ai-assistant-grid { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 20px; align-items: start; }
@media (max-width: 960px) { .ai-assistant-grid { grid-template-columns: minmax(0, 1fr); } }
@keyframes aiAssistantPulse { 0%, 100% { opacity: 1 } 50% { opacity: .4 } }
.ai-assistant-dots span { display: inline-block; width: 6px; height: 6px; margin-right: 4px; border-radius: 50%; background: #BDBDBD; animation: aiAssistantPulse 1.2s ease-in-out infinite; }
.ai-assistant-dots span:nth-child(2) { animation-delay: .2s } .ai-assistant-dots span:nth-child(3) { animation-delay: .4s }
@media (prefers-reduced-motion: reduce) { .ai-assistant-dots span { animation: none; } }
`;

// Plain-text answer → paragraphs and "- " bullet lists (no HTML from the model is ever rendered).
function AnswerText({ text }) {
  const blocks = [];
  for (const raw of String(text || '').split('\n')) {
    const line = raw.replace(/\*\*/g, '').trimEnd();
    const bullet = line.match(/^\s*[-•*]\s+(.*)$/);
    const last = blocks[blocks.length - 1];
    if (bullet) {
      if (last?.type === 'list') last.items.push(bullet[1]);
      else blocks.push({ type: 'list', items: [bullet[1]] });
    } else if (line.trim()) {
      blocks.push({ type: 'p', text: line.trim() });
    }
  }
  return (
    <div style={{ fontSize: '14px', lineHeight: 1.6, color: '#333333', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {blocks.map((b, i) => (b.type === 'list' ? (
        <ul key={i} style={{ margin: 0, paddingLeft: '20px' }}>{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>
      ) : <p key={i} style={{ margin: 0 }}>{b.text}</p>))}
    </div>
  );
}

function Turn({ turn, onAsk, busy }) {
  const conf = CONFIDENCE[turn.confidence];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ alignSelf: 'flex-end', maxWidth: '85%', backgroundColor: '#FFEBEE', color: '#333333', padding: '10px 14px', borderRadius: '12px 12px 4px 12px', fontSize: '14px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {turn.question}
      </div>
      <div style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', border: '1px solid #EEEEEE', borderRadius: '12px 12px 12px 4px', padding: '12px 14px', backgroundColor: '#FAFAFA' }}>
        {turn.status === 'pending' && (
          <div className="ai-assistant-dots" role="status" aria-label="AI odpovídá"><span /><span /><span /></div>
        )}
        {turn.status === 'error' && (
          <div role="alert" style={{ display: 'flex', gap: '8px', color: '#C62828', fontSize: '14px' }}>
            <AlertCircle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} /><span>{turn.error}</span>
          </div>
        )}
        {turn.status === 'done' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <AnswerText text={turn.answer} />
            {turn.references?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {turn.references.map((r) => (
                  <Link key={`${r.type}:${r.id}`} to={REFERENCE_PATHS[r.type]?.(r.id) || '/'} style={{ ...chip, padding: '4px 10px', fontSize: '12px', color: ACCENT, borderColor: '#FFCDD2' }}>
                    {r.label}
                  </Link>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
              {conf && <span style={{ fontSize: '12px', fontWeight: 600, color: conf.color, backgroundColor: conf.bg, padding: '3px 8px', borderRadius: '6px' }}>{conf.label}</span>}
              {turn.followUps?.map((q) => (
                <button key={q} type="button" style={{ ...chip, padding: '4px 10px', fontSize: '12px' }} disabled={busy} onClick={() => onAsk(q)}>{q}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ToolsCard({ onOpenIntake }) {
  const tools = [
    { icon: FileUp, name: 'Chytrý příjem dokumentů', text: 'Nahrajte smlouvu, fakturu nebo protokol – AI ho přečte, navrhne zákazníka či trenažér a uloží ho na správné místo.', action: true },
    { icon: Link2, name: 'Párování dokladů dodavatelů', text: 'Faktury a dodací listy spárujete s položkami v detailu objednávky.' },
    { icon: FileEdit, name: 'Úprava PDF', text: 'Úpravy textu a stránek najdete u každého PDF dokumentu.' },
    { icon: FileText, name: 'AI shrnutí', text: 'Přehled stavu a chybějících údajů v detailu zákazníka a trenažéru.' },
  ];
  return (
    <section style={card} aria-labelledby="ai-tools-title">
      <h2 id="ai-tools-title" style={sectionTitle}>Další AI nástroje</h2>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {tools.map(({ icon, name, text, action }) => { const Icon = icon; return (
          <li key={name} style={{ display: 'flex', gap: '10px' }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: '#FFEBEE', color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={16} aria-hidden="true" />
            </span>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#333333' }}>{name}</p>
              <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#666666', lineHeight: 1.45 }}>{text}</p>
              {action && (
                <button type="button" onClick={onOpenIntake} style={{ ...chip, marginTop: '8px', borderColor: ACCENT, color: ACCENT, fontWeight: 600 }}>
                  <FileUp size={14} aria-hidden="true" /> Nahrát dokument
                </button>
              )}
            </div>
          </li>
        ); })}
      </ul>
    </section>
  );
}

function UsageCard({ status, usage }) {
  if (!status && !usage) return null;
  const totals = (usage?.items || []).reduce((t, i) => ({
    calls: t.calls + i.calls, failed: t.failed + i.failed, tokens: t.tokens + i.inputTokens + i.outputTokens,
  }), { calls: 0, failed: 0, tokens: 0 });
  const cell = { padding: '6px 4px', borderBottom: '1px solid #F0F0F0', textAlign: 'right', whiteSpace: 'nowrap' };
  return (
    <section style={card} aria-labelledby="ai-usage-title">
      <h2 id="ai-usage-title" style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: '6px' }}>
        <BarChart3 size={14} aria-hidden="true" /> Využití AI
      </h2>
      {status && (
        <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#555555' }}>
          {status.configured ? <>Model <strong>{status.model}</strong> · dnes {fmtInt(status.usedToday)} požadavků</> : 'AI není nastavená (chybí API klíč).'}
        </p>
      )}
      {usage && (
        usage.items.length === 0 ? <p style={{ margin: 0, fontSize: '13px', color: '#888888' }}>Za posledních {usage.days} dní žádné volání.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', color: '#444444' }}>
              <caption style={{ textAlign: 'left', fontSize: '12px', color: '#888888', paddingBottom: '6px' }}>Posledních {usage.days} dní, odhad ceny dle ceníku</caption>
              <thead>
                <tr>
                  <th scope="col" style={{ ...cell, textAlign: 'left' }}>Funkce</th>
                  <th scope="col" style={cell}>Volání</th>
                  <th scope="col" style={cell}>Chyby</th>
                  <th scope="col" style={cell}>Tokeny</th>
                  <th scope="col" style={cell}>USD</th>
                </tr>
              </thead>
              <tbody>
                {usage.items.map((i) => (
                  <tr key={`${i.feature}:${i.model}`}>
                    <th scope="row" style={{ ...cell, textAlign: 'left', fontWeight: 400 }} title={`${i.feature} · ${i.model}`}>{featureLabel(i.feature)}</th>
                    <td style={cell}>{fmtInt(i.calls)}</td>
                    <td style={cell}>{fmtInt(i.failed)}</td>
                    <td style={cell}>{fmtInt(i.inputTokens + i.outputTokens)}</td>
                    <td style={cell}>{fmtUsd(i.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 600 }}>
                  <th scope="row" style={{ ...cell, textAlign: 'left', borderBottom: 'none' }}>Celkem</th>
                  <td style={{ ...cell, borderBottom: 'none' }}>{fmtInt(totals.calls)}</td>
                  <td style={{ ...cell, borderBottom: 'none' }}>{fmtInt(totals.failed)}</td>
                  <td style={{ ...cell, borderBottom: 'none' }}>{fmtInt(totals.tokens)}</td>
                  <td style={{ ...cell, borderBottom: 'none' }}>{fmtUsd(usage.totalCostUsd)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </section>
  );
}

let turnSeq = 0;

export default function AiAssistant() {
  const [turns, setTurns] = useState([]);
  const [question, setQuestion] = useState('');
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [status, setStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const endRef = useRef(null);
  const conversation = useRef(0);

  const busy = turns.some((t) => t.status === 'pending');

  useEffect(() => {
    let alive = true;
    aiApi('/status').then((d) => { if (alive) setStatus(d); }).catch(() => {});
    // 403 for ordinary users – the cost overview is then simply not shown
    aiApi('/usage?days=30').then((d) => { if (alive) setUsage(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!turns.length) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    endRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'end' });
  }, [turns.length]);

  const ask = async (text) => {
    const q = String(text || '').trim().slice(0, QUESTION_MAX);
    if (!q || busy) return;
    const history = turns.filter((t) => t.status === 'done').slice(-HISTORY_PAIRS)
      .map((t) => ({ question: t.question.slice(0, HISTORY_TEXT_MAX), answer: t.answer.slice(0, HISTORY_TEXT_MAX) }));
    const id = ++turnSeq;
    const conv = conversation.current;
    setTurns((prev) => [...prev, { id, question: q, status: 'pending' }]);
    setQuestion('');
    const update = (patch) => {
      if (conversation.current !== conv) return;
      setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    };
    try {
      const data = await aiApi('/insights/ask', { method: 'POST', body: { question: q, history } });
      update({ status: 'done', ...data });
      setStatus((s) => (s ? { ...s, usedToday: s.usedToday + 1 } : s));
    } catch (err) {
      update({ status: 'error', error: err.message || 'Dotaz se nepodařilo zpracovat.' });
    }
  };

  const reset = () => {
    conversation.current += 1;
    setTurns([]);
    setQuestion('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      ask(question);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: 1200 }}>
      <style>{PAGE_CSS}</style>
      <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: ACCENT, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles size={20} aria-hidden="true" />
          </span>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#222222', margin: 0 }}>AI asistent</h1>
            <p style={{ fontSize: '14px', color: '#666666', margin: 0 }}>Ptejte se na zákazníky, trenažéry, objednávky a odvozy. Odpovídá Claude jen z dat v ERP.</p>
          </div>
        </div>
        {turns.length > 0 && (
          <button type="button" onClick={reset} style={{ ...chip, padding: '8px 14px' }}>
            <RotateCcw size={14} aria-hidden="true" /> Nová konverzace
          </button>
        )}
      </header>

      {notice && (
        <div role="status" style={{ ...card, padding: '10px 14px', borderColor: '#C8E6C9', backgroundColor: '#F1F8E9', color: '#2E7D32', fontSize: '14px', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice('')} style={{ background: 'none', border: 'none', color: '#2E7D32', cursor: 'pointer', fontSize: '13px' }}>Zavřít</button>
        </div>
      )}

      <div className="ai-assistant-grid">
        <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }} aria-label="Konverzace s AI">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }} aria-live="polite">
            {turns.length === 0 && (
              <div>
                <p style={{ margin: '0 0 10px', fontSize: '14px', color: '#555555' }}>Zkuste například:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {EXAMPLES.map((q) => <button key={q} type="button" style={chip} onClick={() => ask(q)}>{q}</button>)}
                </div>
              </div>
            )}
            {turns.map((t) => <Turn key={t.id} turn={t} onAsk={ask} busy={busy} />)}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); ask(question); }} style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid #EEEEEE', paddingTop: '14px' }}>
            <label htmlFor="ai-question" style={{ fontSize: '13px', fontWeight: 600, color: '#555555' }}>Vaše otázka</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <textarea
                id="ai-question"
                value={question}
                onChange={(e) => setQuestion(e.target.value.slice(0, QUESTION_MAX))}
                onKeyDown={onKeyDown}
                rows={2}
                maxLength={QUESTION_MAX}
                placeholder="Např. Které trenažéry čekají na podpis smlouvy?"
                style={{ flex: 1, minWidth: 0, resize: 'vertical', padding: '10px 12px', borderRadius: '8px', border: '1px solid #E0E0E0', fontSize: '16px', fontFamily: 'inherit', lineHeight: 1.4 }}
              />
              <button
                type="submit"
                disabled={busy || !question.trim()}
                aria-label="Odeslat otázku"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minWidth: 44, height: 44, padding: '0 14px', borderRadius: '8px', border: 'none', backgroundColor: ACCENT, color: '#FFFFFF', fontWeight: 600, cursor: busy || !question.trim() ? 'not-allowed' : 'pointer', opacity: busy || !question.trim() ? 0.55 : 1 }}
              >
                <Send size={16} aria-hidden="true" />
              </button>
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: '#999999' }}>Enter odešle, Shift+Enter nový řádek · {question.length}/{QUESTION_MAX}</p>
          </form>
        </section>

        <aside style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
          <ToolsCard onOpenIntake={() => setIntakeOpen(true)} />
          <UsageCard status={status} usage={usage} />
        </aside>
      </div>

      <AiDocumentIntake
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
        onSaved={() => { setIntakeOpen(false); setNotice('Dokument byl uložen.'); }}
      />
    </div>
  );
}
